import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, ExternalLink, Clock, Users, RefreshCcw, Cpu } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { useLang } from "../i18n/LanguageContext";
import { TREATMENTS, IMAGES } from "../content/siteContent";
import { SpeakButton } from "../components/SpeakButton";
import { TREATMENT_DETAILS } from "../content/details";
import { locDetail } from "../content/details_i18n";
import { SEO } from "../components/SEO";
import { locT, locCat } from "../content/i18n_extra";

export default function TreatmentDetailPage() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const item = TREATMENTS.find((x) => x.id === id);

  if (!item) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-40 text-center px-6">
          <p className="text-sub">—</p>
          <Link to="/" className="text-brand font-semibold text-sm mt-4 inline-block">2D Healthcare</Link>
        </div>
      </div>
    );
  }

  const c = locT(item, lang);
  const cat = locCat(item, lang);
  const d = locDetail(id, lang, TREATMENT_DETAILS[id]);

  return (
    <div className="min-h-screen bg-white" data-testid={`treatment-detail-${id}`}>
      <SEO
        title={c.title}
        description={c.desc}
        path={`/tedavi/${id}`}
        image={item.img || "/og-cover.jpg"}
        type="article"
        keywords={`${c.title}, ${c.tag}, japonyada kanser tedavisi, cancer treatment japan, i-cell networks, 2d healthcare`}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "MedicalWebPage",
            name: c.title,
            description: c.desc,
            medicalAudience: { "@type": "MedicalAudience", audienceType: "Patient" },
            inLanguage: lang,
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "2D Healthcare", item: "https://japonyadakansertedavisi.com/" },
              { "@type": "ListItem", position: 2, name: c.title, item: `https://japonyadakansertedavisi.com/tedavi/${id}` },
            ],
          },
        ]}
      />
      <Navbar />

      <section className="relative pt-28 pb-16 bg-ice overflow-hidden">
        <div className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full blur-3xl" style={{ backgroundColor: `${item.accent}30` }} />
        <div className="relative max-w-5xl mx-auto px-6">
          <Link to="/" data-testid="detail-back" className="inline-flex items-center gap-1.5 text-sm font-semibold text-sub hover:text-brand transition-colors">
            <ArrowLeft size={15} /> {t("detail_back")}
          </Link>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="mt-6">
            <span className="inline-block text-[10px] font-bold tracking-widest uppercase px-3 py-1.5 rounded-full" style={{ backgroundColor: `${item.accent}15`, color: item.accent }}>{cat}</span>
            <h1 className="mt-4 font-heading text-4xl sm:text-5xl font-bold tracking-tight text-ink text-balance">{c.title}</h1>
            <p className="mt-3 text-sm font-semibold" style={{ color: item.accent }}>{c.tag}</p>
          </motion.div>
        </div>
      </section>

      {item.img && (
        <div className="max-w-5xl mx-auto px-6 -mt-2">
          <motion.img
            src={item.img}
            alt={c.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="w-full h-64 sm:h-80 object-cover rounded-3xl border border-black/5"
          />
        </div>
      )}

      <div className="max-w-5xl mx-auto px-6 mt-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex items-center gap-4 bg-ice rounded-3xl border border-brand/15 px-6 py-4 shadow-lg shadow-brand/10"
          data-testid="treatment-video"
        >
          <motion.img
            src={IMAGES.mascot}
            alt="Tudi"
            animate={{ y: [0, -8, 0] }}
            transition={{ repeat: Infinity, duration: 3.5, ease: "easeInOut" }}
            className="w-14 h-14 object-contain drop-shadow-lg"
          />
          <div className="flex-1 min-w-0">
            <p className="font-heading font-bold text-ink text-sm">{c.title}</p>
            <p className="text-xs text-sub truncate">{d.how?.slice(0, 90)}…</p>
          </div>
          <SpeakButton
            getText={() => `${c.title}. ${d.how || ""} ${d.whoFor || ""}`}
            label={t("tudi_listen_page")}
            testid="treatment-speak"
          />
        </motion.div>
      </div>

      <section className="max-w-5xl mx-auto px-6 py-14 grid lg:grid-cols-3 gap-6">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }} className="lg:col-span-2 space-y-6">
          <div className="bg-[#F9FBFD] rounded-3xl border border-black/5 p-8">
            <div className="flex items-center gap-2.5 mb-4">
              <span className="w-9 h-9 rounded-2xl flex items-center justify-center" style={{ backgroundColor: `${item.accent}15`, color: item.accent }}><Cpu size={17} /></span>
              <h2 className="font-heading text-lg font-bold text-ink">{t("detail_how")}</h2>
            </div>
            <p className="text-sm text-ink/65 leading-relaxed">{d.how}</p>
            <ul className="mt-5 grid sm:grid-cols-2 gap-x-4 gap-y-2">
              {c.points.map((p, j) => (
                <li key={j} className="flex items-start gap-2 text-[13px] font-medium text-ink/75">
                  <span className="mt-0.5 w-[18px] h-[18px] rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: `${item.accent}18`, color: item.accent }}>
                    <Check size={11} strokeWidth={3} />
                  </span>
                  {p}
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-[#F9FBFD] rounded-3xl border border-black/5 p-8">
            <div className="flex items-center gap-2.5 mb-4">
              <span className="w-9 h-9 rounded-2xl flex items-center justify-center" style={{ backgroundColor: `${item.accent}15`, color: item.accent }}><Users size={17} /></span>
              <h2 className="font-heading text-lg font-bold text-ink">{t("detail_who")}</h2>
            </div>
            <ul className="space-y-2.5">
              {(d.whoFor || []).map((w, j) => (
                <li key={j} className="flex items-start gap-2.5 text-sm text-ink/70">
                  <span className="mt-1.5 w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: item.accent }} />
                  {w}
                </li>
              ))}
            </ul>
            {c.targets && (
              <div className="mt-5 flex flex-wrap gap-1.5">
                {c.targets.map((tg, j) => (
                  <span key={j} className="px-2.5 py-1 rounded-full text-[11px] font-semibold border" style={{ borderColor: `${item.accent}40`, color: item.accent, backgroundColor: `${item.accent}0D` }}>{tg}</span>
                ))}
              </div>
            )}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }} className="space-y-6">
          <div className="bg-white rounded-3xl border border-black/10 p-7 shadow-sm">
            <div className="flex items-center gap-2.5 mb-3">
              <span className="w-9 h-9 rounded-2xl bg-brand/10 text-brand flex items-center justify-center"><Clock size={17} /></span>
              <h3 className="font-heading font-bold text-ink">{t("detail_duration")}</h3>
            </div>
            <p className="text-sm text-ink/65 leading-relaxed">{d.duration}</p>
          </div>
          <div className="bg-white rounded-3xl border border-black/10 p-7 shadow-sm">
            <div className="flex items-center gap-2.5 mb-3">
              <span className="w-9 h-9 rounded-2xl bg-brand/10 text-brand flex items-center justify-center"><RefreshCcw size={17} /></span>
              <h3 className="font-heading font-bold text-ink">{t("detail_after")}</h3>
            </div>
            <p className="text-sm text-ink/65 leading-relaxed">{d.after}</p>
          </div>
          <Link
            to="/#iletisim"
            data-testid="detail-cta"
            className="group w-full h-13 py-4 rounded-full bg-brand text-white font-heading font-semibold text-sm flex items-center justify-center gap-2 hover:bg-branddeep transition-colors shadow-lg shadow-brand/25"
          >
            {t("detail_cta")}
            <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
          </Link>
          <a href={item.link} target="_blank" rel="noopener noreferrer" data-testid="detail-external" className="w-full py-3.5 rounded-full border border-black/10 text-ink font-heading font-semibold text-sm flex items-center justify-center gap-2 hover:border-brand/40 hover:text-brand transition-colors">
            {t("visit_site")} <ExternalLink size={14} />
          </a>
        </motion.div>
      </section>

      <Footer />
    </div>
  );
}
